import { flow } from 'fp-ts/function';
import { array, nonEmptyArray, option, record, string } from 'fp-ts';
import { ServiceResult, useRepositoryQuery, useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { isNightUse } from '@mews-commander/utils';
import { BookableService, isBookableService } from '@mews-commander/legacy-entities/src/ducks/servicesNew';
import { WithEnterpriseIds } from '../../../../../types';

const getIntersectingServices = (enterpriseIds: string[]) =>
    flow(
        (response: ServiceResult) => response.services,
        array.filter(
            (service): service is BookableService & { externalIdentifier: string } =>
                isBookableService(service) && !!service.externalIdentifier
        ),
        option.fromPredicate(array.isNonEmpty),
        option.match(
            () => [],
            flow(
                nonEmptyArray.groupBy(({ externalIdentifier }) => externalIdentifier),
                record.filterMap(
                    flow(
                        option.fromPredicate((services) => {
                            const [head, ...tail] = services;
                            return (
                                enterpriseIds.length === services.length &&
                                tail.every(
                                    (service) =>
                                        service.data.value.timeUnitPeriod === head?.data.value.timeUnitPeriod &&
                                        isNightUse(service) === isNightUse(head)
                                )
                            );
                        }),
                        option.chain(array.head)
                    )
                ),
                record.collect(string.Ord)((_, value) => value)
            )
        )
    );

export const useIntersectingServicesSuspenseQuery = ({ enterpriseIds }: WithEnterpriseIds) => {
    const { data: intersectingServices } = useRepositorySuspenseQuery(
        'services',
        'getAll',
        { enterpriseIds },
        { select: getIntersectingServices(enterpriseIds) }
    );

    return {
        intersectingServices,
        noIntersectingServices: array.isEmpty(intersectingServices),
    };
};

export const useIntersectingServicesQuery = ({ enterpriseIds }: WithEnterpriseIds) => {
    const query = useRepositoryQuery(
        'services',
        'getAll',
        { enterpriseIds },
        { enabled: array.isNonEmpty(enterpriseIds), select: getIntersectingServices(enterpriseIds) }
    );

    return {
        ...query,
        noIntersectingServices: query.isSuccess ? array.isEmpty(query.data) : true,
    };
};
